/**
 * LES PROBLÈMES COURANTS PAR SECTEUR — liste déroulante de la déclaration d'une NC (RQ, 03/10/2026).
 *
 * Choisir un problème remplit l'intitulé ; on précise ensuite si nécessaire.
 *
 * ⚠️ DEUX ORIGINES, ET C'EST VOULU :
 *   · `preanalytique` en a DEUX, en deux groupes à l'écran (RQ, 07/10/2026) :
 *       — le **défaut de la demande** (`PROBLEMES_NC.preanalytique`), rédigé ici ;
 *       — l'**échantillon non conforme**, qui est la liste des critères d'acceptation tenue au
 *         paramétrage du référentiel — elle sert AUSSI la fiche publique des examens.
 *     La séparation n'est pas cosmétique : « le correspondant m'envoie une prescription sans
 *     prélèvement » n'est pas un échantillon à refuser, il n'y a pas d'échantillon. L'inscrire
 *     parmi les critères de refus l'aurait publié sur la fiche publique des examens, où il
 *     n'a aucun sens pour un patient.
 *   · `aqualifier` n'a pas de liste : le déclarant dit qu'il ne classe pas.
 *   Les autres secteurs sont rédigés ICI, d'après les situations courantes d'un laboratoire de
 *   biologie médicale (ISO 15189). C'est une PROPOSITION de départ, à relire par la RQ : le
 *   registre ne compte pas assez de NC pour en tirer les intitulés réels.
 *
 * ⚠️ DEPUIS LE 08/10/2026, CETTE LISTE EST UN SOCLE, PAS LA LISTE VIVANTE. La RQ
 * et le CQ la reformulent, en désactivent des entrées et en ajoutent depuis
 * Paramétrage ; le moteur sert la liste fusionnée (`GET ged/nc-vocabulaire`) et
 * l'écran la passe à `groupesProblemesDe`. Ce qui est écrit ici reste le REPLI
 * si cette lecture échoue — un formulaire sans intitulé à choisir se lirait
 * « il n'y a rien à déclarer ». Le socle recopié côté serveur
 * (`backend/src/data/vocabulaire-nc-socle.ts`) est comparé à celui-ci par
 * `qualite-nc-vocabulaire.test.js`, caractère par caractère.
 *
 * Module PUR : `qualite-problemes-nc.test.js` vérifie que chaque secteur de `NC_SOURCES` est
 * couvert, ou explicitement dispensé.
 */

export interface ProblemeNC { code: string; libelle: string }

const p = (code: string, libelle: string): ProblemeNC => ({ code, libelle });

export const PROBLEMES_NC: Record<string, ProblemeNC[]> = {
  /* LE DÉFAUT DE LA DEMANDE — §7.2.1 et §7.2.6 (RQ, 07/10/2026).
     Ce qui ne va pas AVANT l'échantillon, ou à sa place : la prescription. Aucun
     recouvrement voulu avec les critères du référentiel — le délai d'acheminement
     dépassé, par exemple, y est déjà (`CRIT-DELAI`) et n'est pas repris ici. */
  preanalytique: [
    p('PRE-SANS-ECHANT', 'Prescription reçue sans l’échantillon correspondant'),
    p('PRE-SANS-PRESCRI', 'Échantillon reçu sans prescription'),
    p('PRE-PRESCRI-INCOMPLETE', 'Prescription incomplète (identité, examens, date, prescripteur)'),
    p('PRE-PRESCRI-ILLISIBLE', 'Prescription illisible ou ambiguë'),
    p('PRE-ID-DISCORDANCE', 'Identité de la prescription et de l’échantillon discordantes'),
    p('PRE-HORS-CATALOGUE', 'Examen demandé hors catalogue ou libellé non identifiable'),
    p('PRE-RENSEIGNEMENT', 'Renseignement clinique indispensable à l’examen manquant'),
    p('PRE-HORS-HORAIRE', 'Échantillons remis hors des horaires convenus'),
  ],
  analytique: [
    p('ANA-ABERRANT', 'Résultat aberrant ou incohérent avec la clinique ou l’antériorité'),
    p('ANA-REPETITION', 'Résultat non reproductible à la répétition'),
    p('ANA-INTERFERENCE', 'Interférence suspectée (hémolyse, ictère, lipémie)'),
    p('ANA-DILUTION', 'Dilution ou répétition nécessaire non réalisée'),
    p('ANA-PARAMETRE', 'Mauvais paramètre ou mauvaise méthode sélectionnés'),
    p('ANA-MANIPULATION', 'Erreur de manipulation (pipetage, ordre des échantillons)'),
    p('ANA-ALARME', 'Alarme de l’automate pendant la série'),
    p('ANA-VALIDATION', 'Résultat non validé dans le délai prévu'),
  ],
  postanalytique: [
    p('POST-IDENTITE', 'Erreur d’identité ou de sexe du patient sur le compte rendu'),
    p('POST-DESTINATAIRE', 'Résultat transmis au mauvais patient ou au mauvais prescripteur'),
    p('POST-TRANSCRIPTION', 'Erreur de transcription ou de saisie du résultat'),
    p('POST-DELAI', 'Compte rendu rendu hors délai'),
    p('POST-CRITIQUE', 'Valeur critique non communiquée au prescripteur'),
    p('POST-MENTION', 'Compte rendu sans unité, valeur de référence ou commentaire requis'),
    p('POST-RECTIFICATIF', 'Compte rendu à rectifier après diffusion'),
    p('POST-CONSERVATION', 'Échantillon non retrouvé pour une demande de complément'),
  ],
  equipement: [
    p('EQ-PANNE', 'Panne d’un appareil'),
    p('EQ-DERIVE', 'Dérive ou écart d’étalonnage'),
    p('EQ-METROLOGIE', 'Vérification métrologique non conforme'),
    p('EQ-MAINTENANCE', 'Maintenance préventive non réalisée à la date prévue'),
    p('EQ-ERREUR', 'Message d’erreur ou alarme récurrents'),
    p('EQ-TEMPERATURE', 'Température d’enceinte hors limites (réfrigérateur, congélateur, étuve)'),
    p('EQ-HORS-SERVICE', 'Appareil utilisé alors qu’il était hors service'),
  ],
  eeq: [
    p('EEQ-HORS-LIMITES', 'Résultat hors limites à une évaluation externe (écart ≥ 3)'),
    p('EEQ-SURVEILLANCE', 'Résultat à surveiller à une évaluation externe (écart ≥ 2)'),
    p('EEQ-DELAI', 'Échantillon d’évaluation externe non traité dans le délai'),
    p('EEQ-TRANSMISSION', 'Résultat non transmis à l’organisme'),
    p('EEQ-PARTICIPATION', 'Participation à un programme manquée'),
  ],
  cqi: [
    p('CQI-HORS-LIMITES', 'Contrôle interne hors limites'),
    p('CQI-WESTGARD', 'Règle de Westgard rejetée'),
    p('CQI-TENDANCE', 'Tendance ou biais systématique du contrôle'),
    p('CQI-NON-PASSE', 'Contrôle non passé avant la série'),
    p('CQI-CHANGEMENT-LOT', 'Changement de lot de contrôle sans comparaison'),
    p('CQI-CALIBRATION', 'Calibration à refaire après un contrôle non conforme'),
  ],
  reactif: [
    p('REA-PERIME', 'Réactif ou consommable périmé utilisé'),
    p('REA-LIVRAISON', 'Livraison non conforme (température, emballage, quantité)'),
    p('REA-LOT', 'Lot de réactif défectueux'),
    p('REA-RUPTURE', 'Rupture de stock d’un réactif ou d’un consommable'),
    p('REA-STOCKAGE', 'Réactif mal stocké ou ouvert sans date d’ouverture'),
    p('REA-CONSOMMABLE', 'Consommable défectueux (tube, embout, cuvette)'),
  ],
  sil: [
    p('SIL-INDISPONIBLE', 'Système informatique indisponible ou très lent'),
    p('SIL-INTERFACE', 'Interface automate–SIL en erreur, résultats non remontés'),
    p('SIL-IDENTITE', 'Erreur de saisie d’identité patient au SIL'),
    p('SIL-DOUBLON', 'Résultat perdu ou transmis en double'),
    p('SIL-PARAMETRAGE', 'Erreur de paramétrage (valeurs de référence, unités, formules)'),
    p('SIL-ACCES', 'Droit d’accès inadapté ou compte partagé'),
    p('SIL-SAUVEGARDE', 'Sauvegarde non réalisée ou en échec'),
  ],
  environnement: [
    p('ENV-TEMPERATURE', 'Température d’un local hors limites'),
    p('ENV-HYGIENE', 'Nettoyage ou désinfection non réalisés'),
    p('ENV-ACCUEIL', 'Salle d’attente ou de prélèvement non conforme (hygiène, confidentialité)'),
    p('ENV-DECHETS', 'Déchets d’activité de soins mal triés ou mal stockés'),
    p('ENV-ACCIDENT', 'Accident d’exposition au sang ou blessure'),
    p('ENV-DEVERSEMENT', 'Déversement ou contamination par un produit biologique ou chimique'),
    p('ENV-COUPURE', 'Coupure d’électricité ou d’eau'),
  ],
  rh: [
    p('RH-HABILITATION', 'Poste tenu sans habilitation'),
    p('RH-EFFECTIF', 'Effectif insuffisant sur un poste'),
    p('RH-FORMATION', 'Formation non réalisée ou non enregistrée'),
    p('RH-CONSIGNE', 'Consigne non appliquée'),
    p('RH-PLANNING', 'Planning non respecté ou poste non pourvu'),
  ],
  documentaire: [
    p('DOC-ABSENT', 'Procédure ou mode opératoire absent'),
    p('DOC-PERIME', 'Document périmé utilisé'),
    p('DOC-NON-APPLIQUE', 'Procédure non appliquée'),
    p('DOC-DIFFUSION', 'Document non diffusé ou accusé de lecture manquant'),
    p('DOC-ENREGISTREMENT', 'Enregistrement manquant ou incomplet'),
    p('DOC-COPIE', 'Copie papier non maîtrisée en circulation'),
  ],
  management: [
    p('MGT-REVUE', 'Revue de direction non tenue ou non documentée'),
    p('MGT-AUDIT', 'Audit interne non réalisé à la date prévue'),
    p('MGT-OBJECTIF', 'Objectif qualité ou indicateur non suivi ou non atteint'),
    p('MGT-ACTION', 'Action décidée non menée dans le délai'),
    p('MGT-RISQUE', 'Risque identifié sans traitement ni suivi'),
    p('MGT-RESPONSABILITE', 'Responsabilité ou délégation non définie ou non à jour'),
    p('MGT-DECISION', 'Décision de la direction non appliquée ou non communiquée'),
    p('MGT-IMPARTIALITE', 'Conflit d’intérêts ou atteinte à l’impartialité'),
  ],
  reclamation: [
    p('REC-DELAI', 'Retard de rendu des résultats'),
    p('REC-CONTESTE', 'Résultat contesté par le patient ou le prescripteur'),
    p('REC-ACCUEIL', 'Accueil ou attente jugés insatisfaisants'),
    p('REC-PRELEVEMENT', 'Prélèvement mal supporté (douleur, hématome)'),
    p('REC-DOCUMENT', 'Document remis au patient perdu ou erroné'),
    p('REC-FACTURATION', 'Erreur de facturation'),
  ],
};

/** Les secteurs sans liste fixe, et pourquoi — pour qu'un secteur ajouté ne reste pas sans liste en silence. */
export const SANS_LISTE_FIXE: Record<string, string> = {
  aqualifier: 'le déclarant signale sans classer',
};

/** Un groupe de la liste déroulante — rendu en `optgroup`. */
export interface GroupeProblemes { titre: string; items: ProblemeNC[] }

/** Le titre du groupe des critères du référentiel — nommé ici pour que l'écran ne l'invente pas. */
export const GROUPE_ECHANTILLON = 'Échantillon non conforme (critères du référentiel)';
export const GROUPE_DEMANDE = 'Défaut de la demande';

/**
 * La liste proposée pour un secteur, EN GROUPES.
 *
 * Le pré-analytique en a deux : le défaut de la demande (ici) et l'échantillon non
 * conforme (référentiel). Les autres secteurs n'en ont qu'un, sans titre : un seul
 * groupe nommé ajouterait un intertitre qui ne distingue rien.
 *
 * `paramétrés` est la liste SERVIE PAR LE MOTEUR pour ce secteur (socle livré +
 * ce que le laboratoire a reformulé, désactivé ou ajouté). Quand elle est là,
 * elle REMPLACE `PROBLEMES_NC[source]` — une entrée désactivée au paramétrage
 * ne doit plus être proposée. Quand elle manque (lecture en échec), on garde le
 * socle : proposer la liste livrée vaut mieux que n'en proposer aucune.
 */
export function groupesProblemesDe(
  source: string,
  criteresPreanalytiques: ProblemeNC[],
  parametres?: ProblemeNC[],
): GroupeProblemes[] {
  const duSecteur = parametres ?? PROBLEMES_NC[source] ?? [];
  if (source === 'preanalytique') {
    return [
      { titre: GROUPE_DEMANDE, items: duSecteur },
      { titre: GROUPE_ECHANTILLON, items: criteresPreanalytiques },
    ].filter((g) => g.items.length > 0);
  }
  return duSecteur.length ? [{ titre: '', items: duSecteur }] : [];
}

/** La même liste À PLAT — pour retrouver un problème par son code. */
export function problemesDe(
  source: string,
  criteresPreanalytiques: ProblemeNC[],
  parametres?: ProblemeNC[],
): ProblemeNC[] {
  return groupesProblemesDe(source, criteresPreanalytiques, parametres).flatMap((g) => g.items);
}

/** Ce problème vient-il du référentiel (critère d'échantillon) plutôt que d'ici ? */
export function estCritereReferentiel(code: string, criteresPreanalytiques: ProblemeNC[]): boolean {
  return criteresPreanalytiques.some((c) => c.code === code);
}

/**
 * LES SECTEURS D'UN PROCESSUS (RQ, 13/10/2026 : « relie la liste déroulante des
 * propositions de NC au Processus concerné, et non à la source »).
 *
 * Les listes restent TENUES par secteur — le paramétrage, les codes et le socle
 * serveur n'ont pas bougé. Ce qui change, c'est ce qui les CHOISIT : le processus
 * de la NC, par la table `PROCESSUS_SUGGERE` lue à l'envers. Le processus
 * « Analytique » rassemble ainsi les listes `analytique`, `cqi` et `eeq` ;
 * « Pré-analytique » celles de `preanalytique` et de `reactif`.
 *
 * La table est PASSÉE EN ARGUMENT et non importée : ce module reste pur (il se
 * teste sans le reste de `meta.ts`), et la seule table qui fait foi est celle
 * qu'on lui donne. Un processus sans secteur (« Maîtrise des achats »…) n'a
 * pas de liste : on ne range pas au hasard.
 */
export function secteursDuProcessus(processus: string, suggere: Record<string, string>): string[] {
  const nom = String(processus || '').trim();
  if (!nom) return [];
  return Object.keys(suggere).filter((secteur) => suggere[secteur] === nom);
}

/**
 * La liste proposée pour un PROCESSUS, en groupes.
 *
 * Un seul secteur : ses groupes tels quels (sans intertitre, sauf le
 * pré-analytique qui garde les siens). Plusieurs : chaque secteur devient un
 * groupe titré de son libellé, pour que « Contrôle interne hors limites » ne se
 * mélange pas à « Résultat aberrant » sans dire d'où il vient. `parametres` est
 * la liste servie par le moteur, secteur par secteur (voir `groupesProblemesDe`).
 */
export function groupesProblemesDuProcessus(
  processus: string,
  suggere: Record<string, string>,
  libelleDe: (secteur: string) => string,
  criteresPreanalytiques: ProblemeNC[],
  parametres?: Record<string, ProblemeNC[] | undefined>,
): GroupeProblemes[] {
  const secteurs = secteursDuProcessus(processus, suggere);
  const plusieurs = secteurs.length > 1;
  return secteurs.flatMap((secteur) => {
    // Les critères d'échantillon ne se rattachent qu'au secteur pré-analytique.
    const groupes = groupesProblemesDe(secteur, secteur === 'preanalytique' ? criteresPreanalytiques : [], parametres?.[secteur]);
    return groupes.map((g) => ((plusieurs && !g.titre) ? { ...g, titre: libelleDe(secteur) } : g));
  });
}

/** La même liste À PLAT — pour retrouver un problème par son code. */
export function problemesDuProcessus(
  processus: string,
  suggere: Record<string, string>,
  libelleDe: (secteur: string) => string,
  criteresPreanalytiques: ProblemeNC[],
  parametres?: Record<string, ProblemeNC[] | undefined>,
): ProblemeNC[] {
  return groupesProblemesDuProcessus(processus, suggere, libelleDe, criteresPreanalytiques, parametres).flatMap((g) => g.items);
}

/** Un groupe de la liste COMPLÈTE : il sait de quel processus il vient. */
export interface GroupeProblemesProcessus extends GroupeProblemes { processus: string }

/**
 * TOUTE LA LISTE, quand aucun processus concerné n'est choisi (RQ, 13/10/2026 :
 * « il faut que la liste affiche tous les problèmes, et non aucun »).
 *
 * Rangée par processus, dans l'ordre de la cartographie (`processus`), chaque
 * groupe titré du nom du processus — et du sous-titre d'origine quand il y en a
 * un (« Pré-analytique — Défaut de la demande »). Chaque groupe porte son
 * processus : choisir un problème peut alors renseigner le processus concerné.
 * Un même problème n'apparaît qu'une fois (sous son premier processus).
 */
export function groupesTousProcessus(
  processus: readonly string[],
  suggere: Record<string, string>,
  libelleDe: (secteur: string) => string,
  criteresPreanalytiques: ProblemeNC[],
): GroupeProblemesProcessus[] {
  const vus = new Set<string>();
  const out: GroupeProblemesProcessus[] = [];
  for (const p of processus) {
    for (const g of groupesProblemesDuProcessus(p, suggere, libelleDe, criteresPreanalytiques)) {
      const items = g.items.filter((x) => !vus.has(x.code));
      items.forEach((x) => vus.add(x.code));
      if (items.length) out.push({ titre: g.titre ? `${p} — ${g.titre}` : p, items, processus: p });
    }
  }
  return out;
}

/** Un groupe de la liste par NATURE : il sait de quelle nature il vient. */
export interface GroupeProblemesNature extends GroupeProblemes { nature: string }

/**
 * LA LISTE « QUEL PROBLÈME ? » SELON LA NATURE (RQ, 13/10/2026).
 *
 * Une nature réunit un ou plusieurs secteurs (« Résultat d'analyse » : analytique,
 * contrôle interne, évaluation externe). Un seul secteur : ses groupes tels
 * quels ; plusieurs : un groupe titré par secteur. Sans nature (ou « Je ne sais
 * pas ») : TOUTE la liste, rangée par nature, chaque groupe portant sa nature —
 * choisir un problème la renseigne. Un problème n'apparaît qu'une fois.
 */
export function groupesParNature(
  nature: string,
  natures: readonly { key: string; label: string; secteurs: readonly string[] }[],
  libelleDe: (secteur: string) => string,
  criteresPreanalytiques: ProblemeNC[],
  secteursEnPlus: readonly string[] = [],
): GroupeProblemesNature[] {
  const deLaNature = (n: { key: string; label: string; secteurs: readonly string[] }, prefixe: boolean): GroupeProblemesNature[] => {
    const secteurs = [...n.secteurs, ...secteursEnPlus.filter((x) => !n.secteurs.includes(x))];
    const plusieurs = secteurs.length > 1;
    return secteurs.flatMap((secteur) => groupesProblemesDe(secteur, secteur === 'preanalytique' ? criteresPreanalytiques : [])
      .map((g) => {
        const titre = g.titre || (plusieurs ? libelleDe(secteur) : '');
        return { titre: prefixe ? (titre ? `${n.label} — ${titre}` : n.label) : titre, items: g.items, nature: n.key };
      }));
  };
  const choisie = natures.find((n) => n.key === nature && n.secteurs.length);
  const brut = choisie ? deLaNature(choisie, false) : natures.filter((n) => n.secteurs.length).flatMap((n) => deLaNature(n, true));
  const vus = new Set<string>();
  return brut.map((g) => ({ ...g, items: g.items.filter((x) => (vus.has(x.code) ? false : (vus.add(x.code), true))) }))
    .filter((g) => g.items.length);
}

